import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { interpolateHexColors } from '../services/colors-interpolator';
import UserIcon from '../icons/UserIcon';
import TeamIcon from '../icons/TeamIcon';
import { getTeamLogo } from '../atoms/TeamLogo';
import ChartMarker from '../atoms/ChartMarker';
import ChartImageMarker from '../atoms/ChartImageMarker';
import ChartLabel from '../atoms/ChartLabel';
import { getScoreObj, getScoreValue } from '../atoms/LabledScore';
import { getPPAIConfidenceWidth, getScoreTooltip } from '../services/score';
import Team from '../types/Team';
import Player from '../types/Player';
import Position from '../types/Position';
import { RanksType } from '../atoms/Ranks';
import ProjectedPAIScore from '../types/ProjectedPAIScore';

interface PAIChartProps {
  className?: string;
  team?: Team;
  player?: Player;
  position?: Position;
  hasPaiData?: boolean;
  hasProjectedPaiData?: boolean;
  showImageBoundary?: boolean;
}

const START_COLOR = '#ffe086';
const END_COLOR = '#ee1f1e';

const averages = [
  {
    name: 'DII',
    value: 1.6,
  },
  {
    name: 'FCS',
    value: 2.0,
  },
  {
    name: 'GROUP_OF_5',
    value: 2.5,
  },
  {
    name: 'POWER_5',
    value: 3.0,
  },
  {
    name: 'NFL_DRAFT',
    value: 3.5,
  }
];

const useStyles = makeStyles((theme: Theme) => ({
  paiChart: {
    boxSizing: 'border-box',
    overflow: 'auto',
    width: '100%',
    maxWidth: '600px',
    height: '100%',
    margin: '0 auto',
    padding: theme.spacing(4),
  },
  chart: {
    width: '100%',
    minWidth: '400px',
    height: '100%',
    position: 'relative',
  },
  axis: {
    width: '100%',
    height: '4px',
    background: `linear-gradient(to right, ${START_COLOR}, ${END_COLOR})`,
    position: 'absolute',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)',
  },
}), { name: PAIChart.name });

export default function PAIChart (props: PAIChartProps) {
  const {
    className,
    team,
    player,
    position,
    hasPaiData = false,
    hasProjectedPaiData = false,
    showImageBoundary = false,
  } = props;
  const classes = useStyles();

  let teamMarker = null;
  if (team && team.pai && !!team.pai.length) {
    const teamPAI = getScoreValue(team.pai, { position }) || 0;
    const teamPosition = Math.round(teamPAI * 100 / 5);

    teamMarker = (
      <ChartImageMarker
        position={teamPosition}
        image={getTeamLogo(team)}
        iconFallback={TeamIcon}
        tooltipText={`Team Avg. PAI: ${teamPAI.toFixed(2)}`}
        color={interpolateHexColors(START_COLOR, END_COLOR, teamPosition)}
      />
    );
  }

  let playerMarker = null;
  if (!hasPaiData && hasProjectedPaiData && player && player.projectedPAI) {
    // const playerPAI = (getScoreValue(player.projectedPAI, { position }) || 0).toFixed(2);
    const playerProjectedPAI = !!player?.projectedPAI ? getScoreValue(player.projectedPAI, { position }) || 0 : 0;
    const playerPosition = Math.round(playerProjectedPAI * 100 / 5);
    const roundedPlayerProjectedPAI = (Math.round((playerProjectedPAI as number) * 10) / 10);
    const confidenceCoefficient = getPPAIConfidenceWidth(getScoreObj(player.projectedPAI, { position }) as ProjectedPAIScore | undefined);
    const tooltipText = getScoreTooltip(RanksType.PROJECTED_PAI, roundedPlayerProjectedPAI, {confidenceCoefficient});

    playerMarker = (
      <ChartImageMarker
        position={playerPosition}
        image={player.photoUrl}
        iconFallback={UserIcon}
        tooltipText={tooltipText}
        color={interpolateHexColors(START_COLOR, END_COLOR, playerPosition)}
        avatar
        showImageBoundary={showImageBoundary}
        boundaryType='ppai'
      />
    );

  } else if (player && player.pai && !!player.pai.length) {
    const playerPAI = (getScoreValue(player.pai, { position }) || 0).toFixed(2);
    const playerPosition = Math.round(parseFloat(playerPAI) * 100 / 5);

    playerMarker = (
      <ChartImageMarker
        position={playerPosition}
        image={player.photoUrl}
        iconFallback={UserIcon}
        tooltipText={getScoreTooltip(RanksType.PAI, Number(playerPAI))}
        color={interpolateHexColors(START_COLOR, END_COLOR, playerPosition)}
        avatar
        showImageBoundary={showImageBoundary}
        boundaryType='pai'
      />
    );
  }

  return (
    <div className={clsx(classes.paiChart, className)}>
      <div className={classes.chart}>
        <div className={classes.axis} />

        <ChartMarker
          position={0}
          label='0'
          color={START_COLOR}
        />
        <ChartMarker
          position={20}
          label='1.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 20)}
        />
        <ChartMarker
          position={32}
          label='1.6'
          color={interpolateHexColors(START_COLOR, END_COLOR, 32)}
        />
        <ChartMarker
          position={40}
          label='2.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 40)}
        />
        <ChartMarker
          position={50}
          label='2.5'
          color={interpolateHexColors(START_COLOR, END_COLOR, 50)}
        />
        <ChartMarker
          position={60}
          label='3.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 60)}
        />
        <ChartMarker
          position={70}
          label='3.5'
          color={interpolateHexColors(START_COLOR, END_COLOR, 70)}
        />
        <ChartMarker
          position={80}
          label='4.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 80)}
        />
        <ChartMarker
          position={100}
          label='5.0'
          color={END_COLOR}
        />

        {averages.map((average:any, index) => {
          const position = Math.round(average.value * 100 / 5);
          const name = average.name
            .split('_')
            .map((word:string, index:number) => (
              <span key={index}>{word}&nbsp;</span>
            ));

          return (
            <ChartLabel
              key={index}
              position={position}
              label={(<>{name}<br />AVG</>)}
              color={interpolateHexColors(START_COLOR, END_COLOR, position)}
              long={(index + 1) % 2 === 0}
            />
          );
        })}

        {teamMarker}
        {playerMarker}
      </div>
    </div>
  );
}
